import {
  Component,
  Input,
  OnChanges,
  OnInit,
  EventEmitter,
  Output,
  ViewChild,
  OnDestroy,
  ElementRef,
  ChangeDetectorRef
} from '@angular/core';
import { ProfileService, StoreService } from 'core/index';
import { LicensesListService } from './licenses-list.service';

@Component({
  selector: 'lms-licenses-list',
  templateUrl: './licenses-list.component.html',
  styleUrls: ['./licenses-list.component.scss'],
  providers: [LicensesListService]
})
export class LicensesListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() listId: string;
  @Input() options: any;
  @Input() config: any;
  @Input() selectable: boolean;
  @Output() public onSelectedLicenses = new EventEmitter<any>();

  @ViewChild('licensesTable', { static: false }) public licensesTable: ElementRef;

  public toolbarConfig: any;
  public dataSource: any;

  public isAnySelected = false;
  public isHeaderSelectedAll: boolean = false; // Selection of visible and non-visible rows
  public selectedLicenses: any = [];
  public excludedLicenses: any = new Set();

  constructor(
    private licensesListService: LicensesListService,
    private profileService: ProfileService,
    private cd: ChangeDetectorRef
  ) {}

  public async ngOnInit() {
    this.toolbarConfig = {
      list: {
        listId: this.listId,
        download: this.profileService.canExecute('licenses.download'),
        filterRow: true,
        columnChooser: true,
        skeletor: true
      }
    };
  }

  public async ngOnChanges() {
    await this.initializeDatasource();
    if (!this.dataSource.docs.length) {
      this.setSortableTable();
    }
    this.cd.detectChanges();
  }

  public ngOnDestroy(): void {
    StoreService.remove(this.listId);
  }

  async initializeDatasource() {
    const listOptions = {
      ...this.options,
      selectionHandler: (data) => this.selectionHandler(data)
    };
    this.dataSource = await this.licensesListService.getDatasource(this.listId, listOptions);
    this.checkHasSuperLicenses();
  }

  onDatasourceUpdate(): void {
    StoreService.update(this.listId, this.dataSource);
    this.checkHasSuperLicenses();
    this.setSelectAll(false);
  }

  checkHasSuperLicenses(): void {
    const hasSuperLicenses =
      this.dataSource && this.dataSource.docs ? this.dataSource.docs.some((license) => license.isSuperlicense) : false;
    if (hasSuperLicenses) {
      const col = this.dataSource.columns.find((col) => col.source === 'lang');
      col.visible = false;
      col.showInColumnChooser = false;
    }
  }

  selectionHandler(rowData) {
    if (rowData) {
      if (this.excludedLicenses.has(rowData.id)) return false;
      return (
        this.isHeaderSelectedAll ||
        (this.selectedLicenses && this.selectedLicenses.some((license) => license.id === rowData.id))
      );
    }
    return this.isHeaderSelectedAll;
  }

  onRowSelectionChanged(eventDetail): void {
    const { selected, data } = eventDetail;
    if (selected) {
      this.excludedLicenses.delete(data.id);
    } else {
      this.excludedLicenses.add(data.id);
    }
  }

  onSelectionChanged(selection): void {
    const { selected = [], isSelectedAll = false } = selection || {};
    if (isSelectedAll && !this.isHeaderSelectedAll) this.excludedLicenses.clear();
    this.selectedLicenses = selected;
    this.isAnySelected = this.selectedLicenses && this.selectedLicenses.length ? true : false;
    this.emitLicensesSelected();
  }

  onFiltersChanged(): void {
    this.setSelectAll(false);
  }

  setSelectAll(flag): void {
    this.dataSource.selectionHandler = flag ? (data) => this.selectionHandler(data) : null;
    this.excludedLicenses.clear();
    this.selectedLicenses = flag ? this.dataSource.docs : [];
    this.isAnySelected = flag;
    this.isHeaderSelectedAll = flag;
    this.licensesTable.nativeElement.setAllSelected(flag);
    this.emitLicensesSelected();
  }

  emitLicensesSelected(): void {
    this.onSelectedLicenses.emit({
      selectAll: this.isHeaderSelectedAll,
      selected: this.selectedLicenses,
      excluded: this.excludedLicenses,
      total: this.isHeaderSelectedAll
        ? this.dataSource.total - this.excludedLicenses.size
        : this.selectedLicenses.length
    });
  }

  /**
   * Set the sort of columns
   * @private
   */
  private setSortableTable(): void {
    this.setToolbarTable(true);
    this.dataSource.columns = this.licensesListService.getColumns(false);
    StoreService.update(this.listId, this.dataSource);
  }

  /**
   * Set the toolbar of table
   * @param hideSearch
   * @private
   */
  private setToolbarTable(hideSearch: boolean): void {
    this.toolbarConfig = {
      ...this.toolbarConfig,
      list: {
        ...this.toolbarConfig.list,
        download: !hideSearch,
        filterRow: !hideSearch,
        hideSearch: hideSearch,
        hideItemsSelector: hideSearch
      }
    };
  }
}
